import { env } from './env.ts';

/**
 * SNS 공유.
 *
 * 이벤트의 SNS 공유 항목이 쓴다. 플랫폼 코드는 백엔드
 * `ModelEventShare::PLATFORM_ID` 와 짝을 맞춘다.
 *
 * ★ **공유를 진짜로 했는지는 알 수 없다.** 페이스북·엑스·밴드는 공유창을 새 창으로
 *   열 뿐이라 그 안에서 무엇을 했는지 이쪽으로 돌아오지 않는다. 그래서 여기서는
 *   "공유창까지 띄웠는가" 만 돌려주고, 응모 한도로 남용을 막는다(유의사항의
 *   `{공유한도}`). 카카오톡 SDK 와 링크 복사, 그리고 브라우저 기본 공유는
 *   성공/취소를 구분할 수 있으므로 취소는 취소로 돌려준다.
 */

/** 공유 결과. `opened` 는 "띄우기까지는 했다" 는 뜻이다. */
export type ShareResult = 'shared' | 'opened' | 'copied' | 'cancelled' | 'blocked' | 'failed';

/**
 * 결과와 사유.
 *
 * 실패했을 때 **무엇이 잘못됐는지 그대로 들고 온다.** 카카오는 도메인 미등록·
 * 잘못된 이미지 주소 같은 것을 구체적인 문구로 알려주는데, 그걸 삼키면 화면에는
 * "공유창을 열지 못했습니다" 만 남아 원인을 찾을 수 없다.
 */
export interface ShareOutcome {
  result: ShareResult;
  message?: string;
}

export interface ShareContent {
  title: string;
  description: string;
  url: string;
  /** 절대 주소여야 한다. 카카오 피드의 큰 이미지로 쓰인다. */
  imageUrl?: string;
  /**
   * 카카오 공유 확인 토큰.
   *
   * 사용자가 **실제로 메시지를 보내면** 카카오 서버가 이 값을 우리 콜백으로
   * 되돌려준다. 그때 응모권이 나간다 — 버튼만 누르고 창을 닫으면 아무 일도
   * 일어나지 않는다.
   */
  callbackToken?: string;
}

/** 성공으로 볼 것인가 — 응모권을 줄지 판단하는 기준 */
export function isShared(result: ShareResult): boolean {
  return result === 'shared' || result === 'opened' || result === 'copied';
}

function ok(result: ShareResult, message?: string): ShareOutcome {
  return { result, message };
}

/** 결과별 안내 문구. 실패했을 때만 쓴다. */
export const SHARE_MESSAGE: Record<ShareResult, string> = {
  shared: '',
  opened: '',
  copied: '링크를 복사했습니다.',
  cancelled: '공유를 취소했습니다.',
  blocked: '팝업이 차단되었습니다. 브라우저 설정에서 허용해 주세요.',
  failed: '공유창을 열지 못했습니다. 잠시 후 다시 시도해 주세요.',
};

////////////////////////////////////////////////////////////////////////////////
// 공유창
////////////////////////////////////////////////////////////////////////////////

/** 가운데에 뜨는 공유 창. 차단되면 null 이 돌아온다. */
function openPopup(url: string): Window | null {
  const width = 600;
  const height = 650;
  const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);

  return window.open(
    url,
    'urnplay-share',
    `popup=yes,width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`
  );
}

function popupResult(win: Window | null): ShareOutcome {
  return { result: win ? 'opened' : 'blocked' };
}

////////////////////////////////////////////////////////////////////////////////
// 카카오톡
////////////////////////////////////////////////////////////////////////////////

/** 카카오 JS SDK 는 로그인용 REST 키가 아니라 **JavaScript 키**를 쓴다. */
const KAKAO_SDK_SRC = 'https://t1.kakaocdn.net/kakao_js_sdk/2.7.7/kakao.min.js';
/** 카카오가 배포 스니펫에 싣는 해시. 구 프로젝트 `hooks/kakao.ts` 에 있던 값이다. */
const KAKAO_SDK_INTEGRITY =
  'sha384-tJkjbtDbvoxO+diRuDtwRO9JXR7pjWnfjfRn5ePUpl7e7RJCxKCwwnfqUAdXh53p';

interface KakaoShare {
  sendDefault(settings: Record<string, unknown>): Promise<unknown> | void;
}

interface KakaoSdk {
  init(key: string): void;
  isInitialized(): boolean;
  Share: KakaoShare;
}

declare global {
  interface Window {
    Kakao?: KakaoSdk;
  }
}

let kakaoLoading: Promise<KakaoSdk | null> | null = null;

/** SDK 를 기다리는 한도. 넘기면 포기하고 기본 공유로 넘어간다. */
const KAKAO_WAIT_MS = 5000;

/** 창에 올라온 SDK 를 초기화해서 돌려준다. 준비가 안 됐으면 null. */
function readyKakao(): KakaoSdk | null {
  const sdk = window.Kakao;
  if (!sdk) return null;

  try {
    if (!sdk.isInitialized()) sdk.init(env.kakao.jsKey);
    return sdk.isInitialized() ? sdk : null;
  } catch {
    // 키가 잘못됐을 때. 여기서 붙잡지 않으면 화면 전체가 멈춘다.
    return null;
  }
}

/**
 * 카카오 SDK 를 한 번만 불러온다.
 *
 * JavaScript 키가 없으면 시도조차 하지 않는다. 호출자는 기본 공유로 넘어간다.
 *
 * ★ **어떤 경우에도 멈추지 않아야 한다.** 예전에는 이미 로드된 `<script>` 를
 *   다시 찾아 `load` 를 기다렸는데, 그 이벤트는 이미 지나가 다시 오지 않는다.
 *   그러면 `await` 가 영영 풀리지 않아 공유창도 안 뜨고 안내도 없이 **아무 일도
 *   일어나지 않았다.** 지금은 창에 SDK 가 있으면 즉시 쓰고, 없으면 시간을 두고
 *   지켜보다가 한도를 넘기면 포기한다.
 */
function loadKakao(): Promise<KakaoSdk | null> {
  if (!env.kakao.jsKey) return Promise.resolve(null);
  if (kakaoLoading) return kakaoLoading;

  kakaoLoading = new Promise<KakaoSdk | null>((resolve) => {
    const already = readyKakao();
    if (already) {
      resolve(already);
      return;
    }

    if (!document.querySelector<HTMLScriptElement>(`script[src="${KAKAO_SDK_SRC}"]`)) {
      const script = document.createElement('script');
      script.src = KAKAO_SDK_SRC;
      script.async = true;
      // 해시가 어긋나면 브라우저가 스크립트를 통째로 막는다. 그때는 아래 감시가
      // 한도까지 기다렸다가 포기한다 — 버전을 올릴 때 해시도 함께 바꿀 것.
      script.integrity = KAKAO_SDK_INTEGRITY;
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }

    // 스크립트를 우리가 붙였든 이미 있었든 같은 방법으로 지켜본다.
    const started = Date.now();
    const timer = window.setInterval(() => {
      const sdk = readyKakao();
      if (sdk || Date.now() - started > KAKAO_WAIT_MS) {
        window.clearInterval(timer);
        // 실패면 캐시를 비워 다음에 다시 받아볼 수 있게 한다.
        if (!sdk) kakaoLoading = null;
        resolve(sdk);
      }
    }, 100);
  });

  return kakaoLoading;
}

/**
 * SDK 를 미리 받아 둔다.
 *
 * 브라우저는 **사용자가 누른 그 순간**에만 팝업을 열어 준다. 버튼을 누른 뒤에
 * SDK 를 내려받으면 그 사이에 사용자 조작이 끊긴 것으로 보여 카카오 공유창이
 * 차단될 수 있다. 화면에 들어올 때 미리 받아 두면 클릭은 곧바로 이어진다.
 */
export function preloadKakao(): void {
  void loadKakao();
}

async function shareKakao(content: ShareContent): Promise<ShareOutcome> {
  const sdk = await loadKakao();
  // JS 키가 없거나 SDK 를 못 받았으면 브라우저 기본 공유로 넘어간다.
  if (!sdk) return shareNative(content);

  // 채팅방에서 카드를 누르면 이 주소로 간다. 카카오 콘솔의 **사이트 도메인**에
  // 등록된 주소여야 한다 — 등록되지 않으면 링크가 통째로 무시된다.
  const link = { mobileWebUrl: content.url, webUrl: content.url };

  try {
    // v2 SDK 의 sendDefault 는 값을 돌려주지 않는다(동기). await 를 걸어 둔 것은
    // 나중에 Promise 를 돌려주게 바뀌어도 그대로 동작하게 하려는 것뿐이다.
    await sdk.Share.sendDefault({
      objectType: 'feed',
      content: {
        title: content.title,
        description: content.description,
        imageUrl: content.imageUrl ?? '',
        link,
      },
      buttons: [{ title: '이벤트 참여하기', link }],
      // 카카오톡이 없는 기기에서는 설치 안내로 보낸다.
      installTalk: true,
      // 전송이 끝나면 카카오가 이 값을 그대로 우리 콜백으로 보내 준다.
      ...(content.callbackToken ? { serverCallbackArgs: { t: content.callbackToken } } : {}),
    });
    return ok('shared');
  } catch (err) {
    // 카카오는 도메인 미등록·이미지 주소 오류 등을 문구로 알려준다. 그대로 보여
    // 준다 — 삼키면 무엇을 고쳐야 하는지 알 수 없다.
    return ok('failed', err instanceof Error ? err.message : String(err));
  }
}

////////////////////////////////////////////////////////////////////////////////
// 나머지 플랫폼
////////////////////////////////////////////////////////////////////////////////

function shareFacebook(content: ShareContent): ShareOutcome {
  const url = new URL('https://www.facebook.com/sharer/sharer.php');
  url.searchParams.set('u', content.url);
  return popupResult(openPopup(url.toString()));
}

function shareX(content: ShareContent): ShareOutcome {
  const url = new URL('https://twitter.com/intent/tweet');
  url.searchParams.set('text', content.title);
  url.searchParams.set('url', content.url);
  return popupResult(openPopup(url.toString()));
}

function shareBand(content: ShareContent): ShareOutcome {
  const url = new URL('https://band.us/plugin/share');
  url.searchParams.set('body', `${content.title}\n${content.url}`);
  url.searchParams.set('route', content.url);
  return popupResult(openPopup(url.toString()));
}

/** 링크 복사. 클립보드 API 가 막힌 환경을 위해 예전 방식도 남겨 둔다. */
async function copyLink(content: ShareContent): Promise<ShareOutcome> {
  try {
    await navigator.clipboard.writeText(content.url);
    return ok('copied');
  } catch {
    // 권한이 없거나 보안 컨텍스트가 아닐 때
  }

  try {
    const area = document.createElement('textarea');
    area.value = content.url;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(area);
    return ok(copied ? 'copied' : 'failed');
  } catch {
    return ok('failed');
  }
}

/** 브라우저 기본 공유 시트. 모바일에서 가장 자연스럽다. */
async function shareNative(content: ShareContent): Promise<ShareOutcome> {
  if (!navigator.share) return copyLink(content);

  try {
    await navigator.share({
      title: content.title,
      text: content.description,
      url: content.url,
    });
    return ok('shared');
  } catch (err) {
    // 사용자가 취소한 것과 진짜 실패를 구분한다.
    if (err instanceof DOMException && err.name === 'AbortError') return ok('cancelled');
    return copyLink(content);
  }
}

////////////////////////////////////////////////////////////////////////////////

/**
 * 플랫폼별 공유창을 띄운다.
 *
 * @param platform `ModelEventShare::PLATFORM_ID` 값 — kakao / facebook / x / band / link
 */
export async function share(platform: string, content: ShareContent): Promise<ShareOutcome> {
  switch (platform) {
    case 'kakao':
      return shareKakao(content);
    case 'facebook':
      return shareFacebook(content);
    case 'x':
      return shareX(content);
    case 'band':
      return shareBand(content);
    case 'link':
      return copyLink(content);
    default:
      return shareNative(content);
  }
}
